<template>
  <li
    class="organization-tree-node"
    :class="{ 'organization-tree-node--child': level > 1 }"
    role="treeitem"
    :aria-level="level"
    :aria-current="isCurrent ? 'true' : undefined"
    :aria-label="accessibleLabel"
    :tabindex="canEdit ? -1 : 0"
  >
    <button
      v-if="canEdit"
      type="button"
      class="organization-tree-node__surface"
      :class="{ 'organization-tree-node__surface--current': isCurrent }"
      :aria-label="`Открыть рабочее место ${roleLabel}`"
      data-testid="organization-tree-node"
      @click="$emit('select', nodeId)"
    >
      <span class="organization-tree-node__icon" aria-hidden="true">
        <q-icon :name="isCurrent ? 'person_pin_circle' : statusIcon" />
      </span>
      <span class="organization-tree-node__identity">
        <strong>{{ roleLabel }}</strong>
        <span>{{ personLabel }}</span>
      </span>
      <q-badge v-if="isCurrent" outline color="primary" label="Ваше рабочее место" />
      <status-badge :label="statusLabel" :tone="statusTone" :icon="statusIcon" />
      <q-icon name="chevron_right" class="organization-tree-node__open" aria-hidden="true" />
    </button>

    <div
      v-else
      class="organization-tree-node__surface organization-tree-node__surface--readonly"
      :class="{ 'organization-tree-node__surface--current': isCurrent }"
      data-testid="organization-tree-node"
    >
      <span class="organization-tree-node__icon" aria-hidden="true">
        <q-icon :name="isCurrent ? 'person_pin_circle' : statusIcon" />
      </span>
      <span class="organization-tree-node__identity">
        <strong>{{ roleLabel }}</strong>
        <span>{{ personLabel }}</span>
      </span>
      <q-badge v-if="isCurrent" outline color="primary" label="Ваше рабочее место" />
      <status-badge :label="statusLabel" :tone="statusTone" :icon="statusIcon" />
    </div>

    <ul
      v-if="children.length"
      class="organization-tree-node__children"
      role="group"
    >
      <organization-tree-node
        v-for="child in children"
        :key="child.key ?? child.unit_info?.id ?? child.label"
        :node="child"
        :can-edit="canEdit"
        :level="level + 1"
        @select="$emit('select', $event)"
      />
    </ul>
  </li>
</template>

<script>
import StatusBadge from '../ui/StatusBadge.vue'

export default {
  name: 'OrganizationTreeNode',
  components: { StatusBadge },
  emits: ['select'],
  props: {
    node: { type: Object, required: true },
    canEdit: { type: Boolean, default: false },
    level: { type: Number, default: 1 }
  },
  computed: {
    unit() {
      return this.node.unit_info || this.node
    },
    nodeId() {
      return this.node.key ?? this.unit.id
    },
    roleLabel() {
      return this.unit.role || this.node.label || 'Рабочее место'
    },
    children() {
      return Array.isArray(this.node.children) ? this.node.children : []
    },
    invite() {
      return this.unit.invite || { empty: true }
    },
    userLabel() {
      if (typeof this.unit.user === 'string') return this.unit.user
      if (this.unit.user && typeof this.unit.user === 'object') {
        return this.unit.user.display_name || this.unit.user.name || ''
      }
      return ''
    },
    isOccupied() {
      return this.unit.has_user === true || Boolean(this.userLabel)
    },
    isPending() {
      return this.invite.empty === false && this.invite.is_accepted !== true
    },
    isCurrent() {
      return this.unit.is_me === true
    },
    personLabel() {
      if (this.isOccupied) return this.userLabel || 'Сотрудник назначен'
      if (this.isPending) return this.invite.display_name || 'Приглашение отправлено'
      return 'Сотрудник не назначен'
    },
    statusLabel() {
      if (this.isOccupied) return 'Занято'
      return this.isPending ? 'Приглашение' : 'Вакантно'
    },
    statusTone() {
      if (this.isOccupied) return 'success'
      return this.isPending ? 'warning' : 'neutral'
    },
    statusIcon() {
      if (this.isOccupied) return 'person'
      return this.isPending ? 'mail_outline' : 'person_outline'
    },
    accessibleLabel() {
      const current = this.isCurrent ? ', ваше рабочее место' : ''
      return `${this.roleLabel}, ${this.personLabel}, ${this.statusLabel}${current}`
    }
  }
}
</script>

<style scoped>
.organization-tree-node {
  position: relative;
  min-width: 20rem;
  list-style: none;
}

.organization-tree-node--child::before {
  position: absolute;
  top: 1.75rem;
  left: -1.5rem;
  width: 1.5rem;
  border-top: 1px solid var(--sid-color-line-strong);
  content: '';
}

.organization-tree-node__surface {
  display: grid;
  width: min(100%, 48rem);
  min-height: 4.25rem;
  grid-template-columns: 2.5rem minmax(12rem, 1fr) auto auto 1.5rem;
  align-items: center;
  gap: var(--sid-space-3);
  padding: var(--sid-space-3) var(--sid-space-4);
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-medium);
  background: var(--sid-color-surface-0);
  color: var(--sid-color-text-primary);
  font: inherit;
  text-align: left;
}

button.organization-tree-node__surface {
  cursor: pointer;
}

button.organization-tree-node__surface:hover,
button.organization-tree-node__surface:focus-visible {
  border-color: var(--sid-color-brand-600);
  background: var(--sid-color-brand-50);
  outline: none;
}

.organization-tree-node__surface--current {
  border-left: 4px solid var(--sid-color-brand-600);
}

.organization-tree-node__surface--readonly {
  grid-template-columns: 2.5rem minmax(12rem, 1fr) auto auto;
}

.organization-tree-node__icon {
  display: inline-flex;
  width: 2.5rem;
  height: 2.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--sid-color-surface-subtle);
  color: var(--sid-color-brand-700);
  font-size: 1.25rem;
}

.organization-tree-node__identity {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.organization-tree-node__identity strong,
.organization-tree-node__identity span {
  overflow-wrap: anywhere;
}

.organization-tree-node__identity span {
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
}

.organization-tree-node__open {
  color: var(--sid-color-text-muted);
}

.organization-tree-node__children {
  position: relative;
  display: grid;
  gap: var(--sid-space-3);
  margin: var(--sid-space-3) 0 0 1.5rem;
  padding: 0 0 0 1.5rem;
}

.organization-tree-node__children::before {
  position: absolute;
  top: -0.75rem;
  bottom: 2.1rem;
  left: 0;
  border-left: 1px solid var(--sid-color-line-strong);
  content: '';
}

@media (max-width: 599px) {
  .organization-tree-node {
    min-width: 17rem;
  }

  .organization-tree-node__surface,
  .organization-tree-node__surface--readonly {
    grid-template-columns: 2.25rem minmax(10rem, 1fr);
    gap: var(--sid-space-2);
    padding: var(--sid-space-3);
  }

  .organization-tree-node__surface > :deep(.q-badge),
  .organization-tree-node__surface > :deep(.status-badge) {
    grid-column: 2;
    justify-self: start;
  }

  .organization-tree-node__open {
    display: none;
  }

  .organization-tree-node__children {
    margin-left: 0.75rem;
    padding-left: 1rem;
  }

  .organization-tree-node--child::before {
    left: -1rem;
    width: 1rem;
  }
}
</style>
